<script setup lang="ts">
    import { ImageUploader } from '@deegital/js-trustup-io-uploads';
    import { storeToRefs } from 'pinia';

    import ProductImage from '@/app/inventory/models/ProductImage';
    import ProductImageSchema from '@/app/inventory/schemas/ProductImageSchema';
    import { ModelType } from '@/app/base/schemas/BaseSchema';
    import EndpointFactory from '@/app/factories/EndpointFactory';
    import { makeId } from '~/utils/string-helpers';

    const { load } = useProductImageSelectStore();
    const { loading, collection } = storeToRefs(useProductImageSelectStore());
    const inputFileId = ref(makeId());
    const inputFile = ref<HTMLInputElement | null>(null);
    const endpoint = EndpointFactory.make(ModelType.PRODUCT_IMAGES);
    const imageUploader = new ImageUploader({
        dimensions: {
            width: 400,
            height: 400,
        },
    });
    const submenu = ref<SubmenuInterface | null>(null);
    const emit = defineEmits(['selected']);
    const uploading = ref<boolean>(false);
    const selected = ref<ProductImage | null>(null);

    const props = withDefaults(
        defineProps<{
            selected?: ProductImage | null;
            clickabled?: boolean;
            placeholderIconSize?: string;
        }>(),
        {
            clickabled: true,
            placeholderIconSize: 'text-2xl',
        }
    );

    const isSelected = (productImage: ProductImage) => selected.value?.getId() == productImage.getId();
    const selectProductImage = (productImage: ProductImage) => {
        submenu.value?.toggle();
        const value = isSelected(productImage) ? null : productImage;
        selected.value = value;
        emit('selected', value);
    };

    const openSubmenu = () => {
        submenu.value?.show();
        if (loading.value) {
            load();
        }
    };

    /**
     * Lets a caller open the picker from its own surface. The trigger this component
     * renders is a square thumbnail, which is the whole of its tap target — fine beside
     * a desktop form, far too small as the only way in on a phone, where the row around
     * it reads as the button. Additive: nothing else calls it, and the thumbnail keeps
     * opening the picker on its own.
     */
    defineExpose({ open: openSubmenu });

    const triggerInputFile = () => {
        inputFile.value?.click();
    };

    const onFileUpload = async (event: Event) => {
        event.preventDefault();
        uploading.value = true;
        const uploaderResponse = await imageUploader.get(event.target.files[0]);
        inputFile.value!.value = '';

        const schema = ProductImageSchema.make({
            attributes: {
                image: {
                    name: uploaderResponse.name,
                    source: uploaderResponse.source,
                },
            },
        });

        const response = await endpoint.upload(schema);
        if (response.data) {
            await wait(500);
            selectProductImage(response.data);
            useToasteoSuccess('toasts.upload_success');
        }
        uploading.value = false;
    };

    watch(
        () => props.selected,
        (value) => {
            if (value) {
                // console.log('Select value has changed via props', value, selected.value);
            }
        }
    );

    onMounted(() => {
        if (props.selected) {
            selected.value = props.selected;
        }
        // load();
    });
</script>

<template>
    <Submenu ref="submenu">
        <template #default>
            <div
                class="w-full transition-all rounded cursor-pointer aspect-square shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] border border-gray-200"
                :class="selected ? 'bg-cover bg-center' : 'flex items-center justify-center hover:bg-neutral-200 bg-neutral-100'"
                :style="`background-image: url(${selected ? selected.url : ''})`"
                @click.prevent="clickabled ? openSubmenu() : ''"
            >
                <i
                    v-if="!selected"
                    class="text-gray-400 fa-regular fa-image"
                    :class="placeholderIconSize"
                ></i>
            </div>
        </template>
        <template #submenu>
            <!-- 400px is the width this grid was drawn for and keeps wherever there is
                 room. Capped against the viewport because there is not always room: in a
                 mobile drawer it overflowed the screen by the difference, cutting off the
                 right-hand column of images. `min()` leaves every desktop caller on the
                 exact 400px it had. -->
            <div class="relative w-[min(400px,calc(100vw-2rem))] py-2">
                <div
                    v-if="loading"
                    class="py-12"
                >
                    <Loader />
                </div>
                <div
                    v-else
                    :key="collection?.getInternalId()"
                    class="grid grid-cols-4 gap-2"
                >
                    <label
                        :for="inputFileId"
                        class="flex items-center justify-center w-full transition-all rounded cursor-pointer hover:bg-neutral-200 bg-neutral-100 aspect-square"
                    >
                        <Loader v-if="uploading" />
                        <i
                            v-else
                            class="text-2xl text-gray-400 fa-regular fa-upload"
                        ></i>
                        <input
                            :id="inputFileId"
                            ref="inputFile"
                            type="file"
                            class="hidden"
                            accept="image/png, image/gif, image/jpeg"
                            @change="onFileUpload"
                        />
                    </label>
                    <div
                        v-for="productImage in collection?.get()"
                        :key="productImage.getInternalId()"
                        class="relative bg-center bg-cover cursor-pointer aspect-square"
                        :style="`background-image: url(${productImage.url})`"
                        @click.prevent="selectProductImage(productImage)"
                    >
                        <div
                            v-if="isSelected(productImage)"
                            class="absolute top-0 right-0 flex items-center justify-center w-5 h-5 -mt-2 -mr-2 rounded-full bg-primary"
                        >
                            <i class="fa-solid fa-sharp fa-check text-[.6rem] text-white" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Submenu>
</template>
